import { Block, NetworkStats, FeeEstimates, Transaction, NetworkSnapshot } from '../models';
import { EventBus } from '../state/EventBus';
import { WebSocketManager, WebSocketConfig } from './WebSocketManager';
import { DataAggregator, APIEndpoints } from './DataAggregator';
import { logger } from '../utils/Logger';

export interface APIConfig {
  mempool: {
    baseUrl: string;
    wsUrl: string;
    rateLimit: number;
  };
  blockchain: {
    wsUrl: string;
  };
  testMode?: boolean;
  mockDataEnabled?: boolean;
}

export interface APIManager {
  // REST endpoints
  fetchLatestBlocks(count: number): Promise<Block[]>;
  fetchNetworkStats(): Promise<NetworkStats>;
  fetchFeeEstimates(): Promise<FeeEstimates>;
  
  // WebSocket management
  connectWebSockets(): Promise<void>;
  disconnect(): void;
  
  // Event emitters
  on(event: 'transaction' | 'block' | 'stats_update', handler: Function): void;
  
  // Initialization
  initialize(): Promise<void>;
  
  // Health check
  healthCheck(): Promise<{ healthy: boolean; details: any }>;
}

export class BitcoinAPIManager implements APIManager {
  private config: APIConfig;
  private eventBus: EventBus;
  private webSocketManager: WebSocketManager;
  private dataAggregator: DataAggregator;
  private initialized = false;
  private statsUpdateInterval: NodeJS.Timeout | null = null;

  constructor(config: APIConfig, eventBus?: EventBus) {
    this.config = config;
    this.eventBus = eventBus || new EventBus();
    
    // Setup endpoints
    const endpoints: APIEndpoints = {
      mempool: {
        baseUrl: config.mempool.baseUrl,
        blocks: '/api/blocks',
        fees: '/api/v1/fees/recommended',
        hashrate: '/api/v1/mining/hashrate', // Base path - specific time period added in fetchHashrate
        mempool: '/api/mempool',
        stats: '/api/stats'
      }
    };

    // Setup WebSocket configuration
    const wsConfig: WebSocketConfig = {
      mempool: {
        wsUrl: config.mempool.wsUrl,
        subscriptions: ['blocks', 'stats', 'mempool-blocks']
      },
      blockchain: {
        wsUrl: config.blockchain.wsUrl
      },
      testMode: config.testMode || false,
      connectionManager: {
        reconnectDelay: 1000,
        maxReconnectAttempts: 5,
        heartbeatInterval: 30000
      }
    };

    this.dataAggregator = new DataAggregator(endpoints);
    this.webSocketManager = new WebSocketManager(wsConfig, this.eventBus);
  }

  async initialize(): Promise<void> {
    if (this.initialized) {
      console.warn('APIManager already initialized');
      return;
    }

    try {
      logger.info('Initializing Bitcoin API Manager', 'APIManager');
      
      // Health check first
      const health = await this.healthCheck();
      if (!health.healthy && !this.config.testMode) {
        throw new Error('API health check failed');
      }

      // Connect WebSockets
      await this.connectWebSockets();
      
      // Start periodic stats updates
      this.startStatsUpdates();
      
      // Fetch initial data
      await this.fetchInitialData();
      
      this.initialized = true;
      logger.info('Initialized successfully', 'APIManager');
      
    } catch (error) {
      console.error('Failed to initialize APIManager:', error);
      throw error;
    }
  }

  async fetchLatestBlocks(count: number): Promise<Block[]> {
    return this.dataAggregator.fetchLatestBlocks(count);
  }

  async fetchNetworkStats(): Promise<NetworkStats> {
    return this.dataAggregator.fetchNetworkStats();
  }

  async fetchFeeEstimates(): Promise<FeeEstimates> {
    return this.dataAggregator.fetchFeeEstimates();
  }

  async connectWebSockets(): Promise<void> {
    return this.webSocketManager.connectWebSockets();
  }

  on(event: 'transaction' | 'block' | 'stats_update', handler: any): void {
    this.eventBus.on(event, handler);
  }

  off(event: 'transaction' | 'block' | 'stats_update', handler: any): void {
    this.eventBus.off(event, handler);
  }

  async getNetworkSnapshot(): Promise<NetworkSnapshot> {
    return this.dataAggregator.getNetworkSnapshot();
  }

  async healthCheck(): Promise<{ healthy: boolean; details: any }> {
    try {
      const health = await this.dataAggregator.healthCheck();
      return {
        healthy: health.overall,
        details: health
      };
    } catch (error) {
      return {
        healthy: false,
        details: { error: (error as Error).message }
      };
    }
  }

  private async fetchInitialData(): Promise<void> {
    try {
      console.log('Fetching initial blockchain data...');
      
      // In test mode, skip actual API calls
      if (this.config.testMode) {
        console.log('Test mode: skipping initial data fetch');
        return;
      }

      const [blocks, stats] = await Promise.all([
        this.fetchLatestBlocks(10),
        this.fetchNetworkStats()
      ]);

      // Emit initial data events
      if (blocks && Array.isArray(blocks)) {
        logger.info(`Emitting ${blocks.length} block events`, 'APIManager');
        blocks.forEach((block, index) => {
          logger.debug(`Block ${index + 1}/${blocks.length}: ${block.height}`, 'APIManager');
          this.eventBus.emit({
            type: 'block',
            timestamp: Date.now(),
            data: block
          });
        });
        logger.debug('All block events emitted', 'APIManager');
      }

      if (stats) {
        this.eventBus.emit({
          type: 'stats_update',
          timestamp: Date.now(),
          data: stats
        });
      }

      console.log(`Loaded ${blocks ? blocks.length : 0} recent blocks and network stats`);
      
    } catch (error) {
      console.error('Failed to fetch initial data:', error);
      if (!this.config.testMode) {
        throw error;
      }
    }
  }

  private startStatsUpdates(): void {
    // Update network stats every 30 seconds
    this.statsUpdateInterval = setInterval(async () => {
      try {
        const stats = await this.fetchNetworkStats();
        this.eventBus.emit({
          type: 'stats_update',
          timestamp: Date.now(),
          data: stats
        });
      } catch (error) {
        console.error('Failed to update network stats:', error);
      }
    }, 30000);
  }

  disconnect(): void {
    console.log('Disconnecting Bitcoin API Manager...');
    
    if (this.statsUpdateInterval) {
      clearInterval(this.statsUpdateInterval);
      this.statsUpdateInterval = null;
    }
    
    this.webSocketManager.disconnect();
    this.dataAggregator.clearCache();
    
    this.initialized = false;
    console.log('Bitcoin API Manager disconnected');
  }

  // Test mode helpers
  simulateTransaction(transaction: Partial<Transaction>): void {
    if (this.config.testMode) {
      const mockTx: Transaction = {
        id: transaction.id || `mock-tx-${Date.now()}`,
        amount: transaction.amount || Math.random() * 1000000,
        fee: transaction.fee || Math.random() * 50000,
        feeRate: transaction.feeRate || Math.random() * 100,
        size: transaction.size || Math.random() * 500,
        timestamp: transaction.timestamp || Date.now(),
        inputs: transaction.inputs || [],
        outputs: transaction.outputs || [],
        status: transaction.status || 'pending'
      };

      this.eventBus.emit({
        type: 'transaction',
        timestamp: Date.now(),
        data: mockTx
      });
    }
  }

  simulateBlock(block: Partial<Block>): void {
    if (this.config.testMode) {
      const mockBlock: Block = {
        height: block.height || Math.floor(Math.random() * 1000000),
        hash: block.hash || `mock-block-${Date.now()}`,
        timestamp: block.timestamp || Date.now(),
        transactionCount: block.transactionCount || Math.floor(Math.random() * 3000),
        size: block.size || Math.random() * 2000000,
        weight: block.weight || Math.random() * 4000000,
        totalFees: block.totalFees || Math.random() * 500000,
        subsidyReward: block.subsidyReward || 625000000,
        difficulty: block.difficulty || Math.random() * 50000000000000,
        nonce: block.nonce || Math.floor(Math.random() * 4294967295),
        merkleRoot: block.merkleRoot || `mock-merkle-${Date.now()}`,
        previousBlockHash: block.previousBlockHash || `mock-prev-${Date.now()}`
      };

      this.eventBus.emit({
        type: 'block',
        timestamp: Date.now(),
        data: mockBlock
      });
    }
  }

  // Getters for inspection
  get isInitialized(): boolean {
    return this.initialized;
  }

  get isTestMode(): boolean {
    return this.config.testMode || false;
  }
}

// Default configuration
export const defaultAPIConfig: APIConfig = {
  mempool: {
    baseUrl: 'https://mempool.space',
    wsUrl: 'wss://mempool.space/api/v1/ws',
    rateLimit: 100 // ms between requests
  },
  blockchain: {
    wsUrl: 'wss://ws.blockchain.info/inv'
  },
  testMode: false,
  mockDataEnabled: false
};

// Singleton instance for convenience
export let apiManager: BitcoinAPIManager;

export function createAPIManager(config: APIConfig = defaultAPIConfig, eventBus?: EventBus): BitcoinAPIManager {
  apiManager = new BitcoinAPIManager(config, eventBus);
  return apiManager;
}